import React, { useState, useRef, useEffect } from 'react';
import './CulturalSafari.css';
import {
  FaUtensils,
  FaHotel,
  FaUsers,
  FaMapSigns,
  FaHandHoldingUsd,
  FaScroll,
  FaPeopleCarry
} from "react-icons/fa";
import { Link } from 'react-router-dom';

const CulturalSafari = () => {
  const [openDay, setOpenDay] = useState(1);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const carouselTrackRef = useRef(null);
  const progressBarRef = useRef(null);
  const carouselIntervalRef = useRef(null);

  const handleDayClick = (dayNumber) => {
    setOpenDay(openDay === dayNumber ? null : dayNumber);
  };

  // Toggle carousel pause state
  const toggleCarouselPause = () => {
    setIsCarouselPaused(!isCarouselPaused);
  };

  // Manual navigation for carousel
  const scrollCarouselUp = () => {
    if (carouselTrackRef.current) {
      const track = carouselTrackRef.current;
      const itemHeight = 280 + 16; // image height + gap
      track.style.transform = `translateY(-${itemHeight}px)`;
    }
  };

  const scrollCarouselDown = () => {
    if (carouselTrackRef.current) {
      const track = carouselTrackRef.current;
      const itemHeight = 280 + 16;
      track.style.transform = `translateY(${itemHeight}px)`;
    }
  };

  // Reset carousel animation when it reaches end
  useEffect(() => {
    const resetAnimation = () => {
      if (carouselTrackRef.current) {
        carouselTrackRef.current.style.transition = 'none';
        carouselTrackRef.current.style.transform = 'translateY(0)';
        setTimeout(() => {
          if (carouselTrackRef.current) {
            carouselTrackRef.current.style.transition = 'transform 30s linear';
          }
        }, 50);
      }
    };

    // Reset animation every 30 seconds (matches animation duration)
    const interval = setInterval(() => {
      if (!isCarouselPaused) {
        resetAnimation();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isCarouselPaused]);

  // Control animation play state
  useEffect(() => {
    if (carouselTrackRef.current) {
      if (isCarouselPaused) {
        carouselTrackRef.current.style.animationPlayState = 'paused';
      } else {
        carouselTrackRef.current.style.animationPlayState = 'running';
      }
    }
    if (progressBarRef.current) {
      if (isCarouselPaused) {
        progressBarRef.current.style.animationPlayState = 'paused';
      } else {
        progressBarRef.current.style.animationPlayState = 'running';
      }
    }
  }, [isCarouselPaused]);

  const itineraryDays = [
    {
      day: 1,
      title: "The Arrival & The Giving of the Shuka",
      subtitle: "Eseriani",
      description: "Your journey begins as you step into the Maasai world, leaving modern comforts behind to embrace simplicity and presence.",
      details: [
        "Arrive at the community's eco-camp in the Loita Hills",
        "Senior elder's welcome blessing in Maa language",
        "Receive your shuka - your garment and symbol of acceptance",
        "First Kijiji Chat: fireside introduction outlining the week's covenant",
        "Night in your inkajijik hut with sounds of the savanna"
      ],
      quote: "You will do, not just watch. You will ask, not assume."
    },
    {
      day: 2,
      title: "The Warrior's Path & The Language of the Earth",
      subtitle: "",
      description: "Learn to read the land as the Maasai do, understanding nature's subtle signs and ancient wisdom.",
      details: [
        "Pre-dawn awakening by warriors for morning cattle duties",
        "4-hour Deep Walk with Morans through the savanna",
        "Track wildlife through broken twigs, bird calls, and plant signs",
        "Carve your own rungu (club) or walking stick",
        "Learn the basics of the adumu (jumping dance)",
        "Fire-making without matches using traditional friction method",
        "Help prepare and share nyama choma (roasted goat)"
      ],
      quote: "The earth speaks to those who know how to listen."
    },
    {
      day: 3,
      title: "The Wisdom of the Bead & The Mud of the Home",
      subtitle: "",
      description: "Enter the women's domain, where stories are woven into beads and homes are built from earth and community.",
      details: [
        "Beadwork session with the women - each color tells a story",
        "Learn symbolic meanings: white (peace), blue (water), red (life)",
        "Help plaster walls of an inkajijik with clay and dung",
        "Evening Kijiji Chat with mothers about family and tradition",
        "Discuss balancing modern education with cultural preservation",
        "Understand women's role in Maasai society and ecology"
      ],
      quote: "The home is built by, and protects, the community."
    },
    {
      day: 4,
      title: "The Herd & The Horizon",
      subtitle: "",
      description: "Experience the daily rhythm of pastoral life, moving with the herds under Africa's vast sky.",
      details: [
        "Sacred pre-dawn goat milking with patient guidance",
        "All-day grazing walk moving with the cattle herd",
        "Learn to read cattle moods and find optimal grazing spots",
        "Taste fresh mursik (fermented milk) from a traditional gourd",
        "Evening star stories about Naiteru-kop and the cattle's origin",
        "Understand the Maasai's deep connection to their livestock"
      ],
      quote: "There is no destination. The walking is the purpose."
    },
    {
      day: 5,
      title: "The Circle Closes & The Fire Stories",
      subtitle: "",
      description: "Contribute to the community and participate in profound cultural exchanges that bridge worlds.",
      details: [
        "Morning community work project (fence mending, spring clearing)",
        "Final Kijiji Chat with the Laiboni (spiritual guide)",
        "Discuss Enkai's balance and modern threats to tradition",
        "Help prepare a communal feast with local ingredients",
        "Sacred evening of oral histories and traditional songs",
        "Learn call-and-response chants around the ceremonial fire"
      ],
      quote: "A story told by firelight enters through the heart, not just the ears."
    },
    {
      day: 6,
      title: "The Silence & The Carrying Forward",
      subtitle: "Kidua",
      description: "Reflect on your transformation and prepare to carry the experience forward into your daily life.",
      details: [
        "Sunrise silent reflection walk alone with your thoughts",
        "Closing circle ceremony with the entire community",
        "Receive hand-written certificate from the Elder's Council",
        "Be given a Maasai name based on your observed character",
        "Guided reflection on carrying the experience forward",
        "Final embrace with your hosts before departure"
      ],
      quote: "You came with a camera. You leave with a story in your heart."
    }
  ];

  // Carousel images data - 6 images as requested
   const carouselImages = [
    {
      id: 1,
      src: "/images/MaaDress.jpeg",
      title: "Traditional Ladies Outfit",
      subtitle: "The beginning of your journey"
    },
    {
      id: 2,
      src: "/images/Maa1.jpeg",
      title: "interaction With Elders",
      subtitle: "Learning ancient traditions"
    },
    {
      id: 3,
      src: "/images/firemaking.jpeg",
      title: "Fire Making",
      subtitle: "Learn Skills"
    },
    {
      id: 4,
      src: "/images/Maa3.jpeg",
      title: "Sacred Herd",
      subtitle: "The rhythm of pastoral life"
    },
    {
      id: 5,
      src: "/images/maa9.jpeg",
      title: "Cultural Dance",
      subtitle: "Generations of knowledge"
    },
    {
      id: 6,
      src: "/images/MaaJumping.jpeg",
      title: "Maasai Jumping",
      subtitle: "Engagement Activities"
    },
     {
      id: 7,
      src: "/images/Maa10.jpeg",
       title: "Maasai Jumping",
      subtitle: "Engagement Activities"
     },
      {
      id: 8,
      src: "/images/Maa7.jpeg",
       title: "Maasai Jumping",
      subtitle: "Engagement Activities"
     },
      {
      id: 9,
      src: "/images/MaaHouse.jpeg",
       title: "Maasai Jumping",
      subtitle: "Engagement Activities"
     },
      {
      id: 10,
      src: "/images/MaaJumping.jpeg",
       title: "Maasai Jumping",
      subtitle: "Engagement Activities"
     },
      {
      id: 11,
      src: "/images/MaaJumping.jpeg",
       title: "Maasai Jumping",
      subtitle: "Engagement Activities"
     },
     {
      id: 11,
      src: "/images/Maa4.jpeg",
       title: "Maasai Jumping",
      subtitle: "Engagement Activities"
     },
  ];

  // Duplicate images for seamless scrolling
  const allCarouselImages = [...carouselImages, ...carouselImages];

  return(
    <div className='MaasaiCultural'>

      {/**HERO NATION 888888888888888888 */}
      <div className='MaasaiHero'>
        {/* Image Container */}
        <div className='HeroMaasai'>
          <img 
            src="/images/CultureHero.png" 
            alt="Cultural Immersion" 
            className='hero-image'
          />
        </div>
        
        {/* HERO Overlay Text */}
        <div className='HeroMaaText'>
          <h2>Live as a Maasai</h2>
          <h3>Walk With us and Find Out</h3>
          <p>Here, you gather firewood with the women, guided on which plants are used. You learn to milk goats at dawn. You practice throwing the rungu (club) with warriors. Knowledge is transferred through action.</p>
        </div>
      </div>

      {/**INTRO SECTION ********************************* */}
     <div className='MaaIntro'>
  {/* Optional decorative dots */}
  <span className="accent-dot"></span>
  <span className="accent-dot"></span>
  <span className="accent-dot"></span>
  
  <h4>Living Maasai</h4>
  <p>The Maasai Immersion is a 6-day program where you join a Maasai community in Kenya. You live and work alongside families, following their daily routine from morning until night.</p>

  <p>You participate in real tasks: <b>milking goats at dawn, collecting firewood with the women, herding cattle with warriors, and building traditional houses.</b> You learn tracking, beadwork, and fire-making directly from community members. The schedule follows their rhythm, not a tourist itinerary.</p>

  <p>The program is led by Maasai elders. It focuses on genuine exchange. Group sizes are capped to promote interaction and minimize distractions. This is a hands-on cultural experience built on respect and direct participation.</p>
</div>

{/******FROM INTRO TO ITENERARY TRANSITION******** */}
  {/* Intro Section */}
      <div className="itinerary-intro">
        <div className="intro-header">
          <span className="intro-label">Epic Journey</span>
          <h2>Walking with the Maasai</h2>
          <p className="intro-subtitle">Inkutuk: The shared journey, the path walked together</p>
        </div>
        
        <div className="intro-content">
          <div className="core-ethos">
            <h3>Core Ethos</h3>
            <p className="ethos-statement">
             This is an invitation to walk beside a chosen Maasai community for six days, governed by their sun, their tasks, and their hospitality. 
              Knowledge is transferred through action, respect is earned through participation, 
              and understanding is forged in the shared silence of the savanna.
            </p>
          </div>
          
          <div className="journey-vessel">
            <h3>The Vessel: Your Journey Explained</h3>
            <div className="vessel-details">
              <div className="vessel-item">
                <span className="vessel-icon"><FaHotel/></span>
                <div>
                  <h4>Accommodation</h4>
                  <p>Sleep in a specially-built guest inkajijik within a family compound. Simple, clean, and sacred—your shelter for the week.</p>
                </div>
              </div>
              
              <div className="vessel-item">
                <span className="vessel-icon"><FaUtensils/></span>
                <div>
                  <h4>Food</h4>
                  <p>Eat what the community eats: ugali, nyama choma, mursik (fermented milk), and wild greens. This sustenance is part of the learning.</p>
                </div>
              </div>
              
              <div className="vessel-item">
                <span className="vessel-icon"><FaUsers/></span>
                <div>
                  <h4>Group Size</h4>
                  <p>Intentionally limited to ensure genuine interaction and minimal cultural disruption. Each group becomes a temporary part of the community.</p>
                </div>
              </div>
              
              <div className="vessel-item">
                <span className="vessel-icon"><FaMapSigns/></span>
                <div>
                  <h4>Your Guide</h4>
                  <p>Not a lecturer, but an Oloiboni (facilitator) from the community—a translator of both language and spirit.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

<div className="daily-itinerary">
  <div className="itinerary-container">
    {/* Left Column - Itinerary (2/3 width) */}
    <div className="itinerary-column">
      <div className="itinerary-header">
        <h2>The Epic Journey: A Daily Chronicle</h2>
        <p className="itinerary-subtitle">Six days of complete immersion and transformation</p>
      </div>
      
      <div className="accordion-container">
        {itineraryDays.map((day) => (
          <div 
            key={day.day} 
            className={`accordion-day ${openDay === day.day ? 'active' : ''}`}
          >
            <div 
              className="accordion-header"
              onClick={() => handleDayClick(day.day)}
            >
              <div className="day-number">
                <span>Day {day.day}</span>
                {day.subtitle && <span className="day-subtitle">{day.subtitle}</span>}
              </div>
              <div className="day-title">
                <h3>{day.title}</h3>
                <p className="day-description">{day.description}</p>
              </div>
              <div className="accordion-toggle">
                <span className="toggle-icon">
                  {openDay === day.day ? '−' : '+'}
                </span>
              </div>
            </div>
            
            <div className="accordion-content">
              <div className="day-details">
                <div className="details-list">
                  <h4>Experience Includes:</h4>
                  <ul>
                    {day.details.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    
    {/* Right Column - Auto-scrolling Image Carousel (1/3 width) */}
    <div className="carousel-column">
      <div className="carousel-header">
        <p className="carousel-subtitle">Moments from the Maasai Experience</p>
      </div>
      
      <div 
        className="vertical-carousel"
        onMouseEnter={() => setIsCarouselPaused(true)}
        onMouseLeave={() => setIsCarouselPaused(false)}
      >
        <div 
          ref={carouselTrackRef}
          className={`carousel-track ${isCarouselPaused ? 'paused' : ''}`}
        >
          {carouselImages.map((image, index) => (
  <div key={image.id} className="carousel-item">
    <img
      src={image.src}
      alt={image.title}
      loading="lazy"
    />
    <div className="carousel-overlay">
      <h4>{image.title}</h4>
      <p>{image.subtitle}</p>
    </div>
  </div>
))}

        </div>
      </div>
      
      <div className="carousel-controls">
        <button 
          className="control-btn up-btn" 
          onClick={scrollCarouselDown}
          aria-label="Scroll up"
        >
          <span className="control-icon">▲</span>
        </button>
        
        <button 
          className="control-btn pause-btn" 
          onClick={toggleCarouselPause}
          aria-label={isCarouselPaused ? "Play carousel" : "Pause carousel"}
        >
          <span className="pause-icon">
            {isCarouselPaused ? '▶' : '❚❚'}
          </span>
        </button>
        
        <button 
          className="control-btn down-btn" 
          onClick={scrollCarouselUp}
          aria-label="Scroll down"
        >
          <span className="control-icon">▼</span>
        </button>
      </div>
      
      <div className="carousel-progress">
        <div 
          ref={progressBarRef}
          className={`progress-bar ${isCarouselPaused ? 'paused' : ''}`}
        />
      </div>
    </div>
  </div>
</div>
   {/* Framework Section */}
      <div className="framework-section">
  {/* Decorative dots */}
  <span className="decorative-dot"></span>
  <span className="decorative-dot"></span>
  <span className="decorative-dot"></span>
  
  <div className="framework-header">
    <h2>The Unseen Framework</h2>
    <p className="framework-subtitle">The ethical foundation that makes this experience possible</p>
  </div>
  
  <div className="framework-grid">
    <div className="framework-card">
      <div className="framework-icon"><FaPeopleCarry/></div>
      <h3>Co-Creation</h3>
      <p>This itinerary was not made in an office. It was drawn in the dirt with elders. A community council approves every guest, every word.</p>
    </div>
    
    <div className="framework-card">
      <div className="framework-icon"><FaHandHoldingUsd/></div>
      <h3>Financial Transparency</h3>
      <p>For every dollar paid, the majority flows directly to the participating families and a community fund for the school and clinic.</p>
    </div>
    
    <div className="framework-card">
      <div className="framework-icon"><FaScroll/></div>
      <h3>Your Pledge</h3>
      <p>You will receive a detailed cultural protocol: how to greet, how to offer and receive, when not to take a photo. You are a guest in a living culture.</p>
    </div>
  </div>
</div>

      {/* CTA Section */}
      <div className="itinerary-cta">
        <div className="cta-content">
          <h2>Are You Ready to Walk the Inkutuk?</h2>
          <p className="cta-text">
            This journey is for those who seek not just to see, but to feel the earth crack underfoot, 
            to taste milk at dawn, to have their worldview quietly, permanently altered.
            <b>A 6-day immersion from $3,200 per person</b>
            The circle awaits.
          </p>
          
          <div className="cta-actions">
            <Link to="/contact">
            <button className="cta-btn primary">
              <span>Begin Your Application</span>
            </button></Link>
            
            <Link   
            onClick={() => {
    const email = "setek@oleninisafaris.com";
    const subject = encodeURIComponent("Maasai Immersion Program Inquiry");
    const body = encodeURIComponent(
      "Hello,\n\nI am interested in the Maasai Immersion program and would like to learn more.\n\nThank you."
    );

    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  }}>
            <button className="cta-btn secondary">
              <span>Contact Our Immersion Curator</span>
            </button>
            </Link>

          </div>
          
          <div className="cta-note">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33334 8 1.33334C4.3181 1.33334 1.33334 4.3181 1.33334 8C1.33334 11.6819 4.3181 14.6667 8 14.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 5.33334V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 10.6667H8.00667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Application includes a brief conversation to ensure mutual fit and readiness</span>
          </div>
        </div>
      </div>

    </div>
  )
};

export default CulturalSafari;